/** @file clip.setKeys: writes any number of keys into a clip, replacing them or merging their nodes. */
import type { CommandDef } from '../apply/commandDef';
import { checkClip } from '../puppetDoc/checkClip';
import { findClip } from '../puppetDoc/findClip';
import { readInput } from '../validate/readInput';
import { checkFrame } from './checkFrame';
import { keyWarnings } from './keyWarnings';
import { readKeys } from './readKeys';
import { restoreKeys } from './restoreKeys';
import { writeKey } from './writeKey';

/**
 * `clip.setKeys { clip, keys, merge }` → `{ frames }`: frames are integers in 0..`frames` (`FRAME_OUT_OF_RANGE`
 * above), each once; unknown nodes are kept with `UNKNOWN_NODE_IN_CLIP`. Inverse: the previous keys at those frames.
 */
export const clipSetKeysCommand: CommandDef<'clip.setKeys'> = {
  name: 'clip.setKeys',
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => {
    const r = readInput(input, path, ['clip', 'keys', 'merge']);
    return { clip: r.str('clip'), keys: readKeys(r.raw('keys'), r.at('keys')), merge: r.oneOf('merge', ['replace', 'mergeNodes'] as const) };
  },
  /** Applies the command to the draft and returns its result and inverse. */
  apply: (draft, input) => {
    const { name, clip } = findClip(draft, input.clip);
    input.keys.forEach((k, i) => { checkFrame(clip, k.frame, `/input/keys/${i}/frame`); });
    const frames = input.keys.map((k) => k.frame);
    const inverse = [restoreKeys(name, clip, frames)];
    for (const k of input.keys) writeKey(clip, k, input.merge);
    checkClip(name, clip);
    return { result: { frames }, warnings: keyWarnings(draft, name, clip, input.keys), inverse };
  },
};
